import { type ReactNode, useId } from 'react'
import cx from 'classnames'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
  ModalSheetTitle,
} from '@/components/ModalSheet'
import { Form } from '@/components/Form'
import { Button } from '@/components/Button'
import { Checkbox } from '@/components/Checkbox'
import { Radio } from '@/components/Radio'
import { TabBar, TabItem } from '@/components/TabBar'
import {
  InfoGatheringDialogFieldGrid,
  InfoGatheringDialogScrollArea,
  InfoGatheringDialogSubsectionHeader,
} from '@/features/jobs/info-gathering/components/InfoGatheringDialog'
import styles from './EditRefundDialog.module.css'
import type { RefundTab } from './EditRefundDialogViewModel'

export interface EditRefundDialogTab {
  id: RefundTab
  label: string
}

export interface EditRefundDialogProps {
  showing: boolean
  onRequestClose?: () => unknown
  hasMultipleTabs: boolean
  tabs: EditRefundDialogTab[]
  activeTab: RefundTab
  activeTabLabel: string
  onTabChange: (tab: RefundTab) => unknown
  personalEditor: ReactNode
  businessEditor: ReactNode
  showBack: boolean
  onBack: () => unknown
  showSkip: boolean
  onSkip: () => unknown
  submitLabel: string
  onSubmit: () => unknown
  isSubmitPending: boolean
}

export function EditRefundDialog({
  showing,
  onRequestClose,
  hasMultipleTabs,
  tabs,
  activeTab,
  activeTabLabel,
  onTabChange,
  personalEditor,
  businessEditor,
  showBack,
  onBack,
  showSkip,
  onSkip,
  submitLabel,
  onSubmit,
  isSubmitPending,
}: EditRefundDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={onRequestClose}
      disableCloseOnOutsideClick
      disableCloseOnEsc
    >
      <ModalSheetHeader withCloseButton bordered={!hasMultipleTabs}>
        <ModalSheetTitle emphasized>Fill in refund info</ModalSheetTitle>
      </ModalSheetHeader>
      {hasMultipleTabs && (
        <TabBar bordered>
          {tabs.map((tab) => (
            <TabItem
              key={tab.id}
              active={activeTab === tab.id}
              onPress={() => onTabChange(tab.id)}
              testId={`edit-refund-tab-${tab.id}`}
            >
              {tab.label}
            </TabItem>
          ))}
        </TabBar>
      )}
      <ModalSheetContent>
        <ModalSheetSection spaced>
          <InfoGatheringDialogScrollArea>
            <Form>
              <div
                className={cx(activeTab !== 'personal' && styles.tabPaneHidden)}
              >
                {personalEditor}
              </div>
              <div
                className={cx(activeTab !== 'business' && styles.tabPaneHidden)}
              >
                {businessEditor}
              </div>
            </Form>
          </InfoGatheringDialogScrollArea>
        </ModalSheetSection>
      </ModalSheetContent>
      <ModalSheetFooter>
        {showBack && (
          <ModalSheetFooterButtonGroup>
            <Button
              label="Back"
              variant="secondary"
              onPress={onBack}
              isDisabled={isSubmitPending}
              testId="edit-refund-back-button"
            />
          </ModalSheetFooterButtonGroup>
        )}
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          {showSkip && (
            <Button
              label={`Skip ${activeTabLabel}`}
              variant="secondary"
              onPress={onSkip}
              isDisabled={isSubmitPending}
              testId="edit-refund-skip-button"
            />
          )}
          <Button
            label={submitLabel}
            variant="primary"
            onPress={onSubmit}
            isPending={isSubmitPending}
            isDisabled={isSubmitPending}
            testId="edit-refund-submit-button"
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

export interface EditRefundMethodOption<TMethod extends string> {
  id: TMethod
  label: string
  testId: string
}

export interface EditRefundMethodPickerProps<TMethod extends string> {
  methods: EditRefundMethodOption<TMethod>[]
  selectedMethod: TMethod | null
  onSelectMethod: (method: TMethod) => unknown
  selectionError: string | null
  children?: ReactNode
}

export function EditRefundMethodPicker<TMethod extends string>({
  methods,
  selectedMethod,
  onSelectMethod,
  selectionError,
  children,
}: EditRefundMethodPickerProps<TMethod>) {
  const radioName = useId()
  return (
    <>
      <InfoGatheringDialogSubsectionHeader>
        Choose the method
      </InfoGatheringDialogSubsectionHeader>
      <div className={styles.methodPicker}>
        {methods.map((method) => (
          <label
            key={method.id}
            className={cx(
              styles.methodCard,
              selectedMethod === method.id && styles.methodCardSelected,
            )}
            data-test={method.testId}
          >
            <Radio
              name={radioName}
              selected={selectedMethod === method.id}
              onSelect={() => onSelectMethod(method.id)}
            />
            <span className={styles.methodCardLabel}>{method.label}</span>
          </label>
        ))}
      </div>
      {selectionError && (
        <div className={styles.methodSelectionError}>{selectionError}</div>
      )}
      {children}
    </>
  )
}

export interface EditRefundDirectDepositSectionProps {
  children?: ReactNode
}

export function EditRefundDirectDepositSection({
  children,
}: EditRefundDirectDepositSectionProps) {
  return (
    <>
      <InfoGatheringDialogSubsectionHeader bordered>
        New bank account
      </InfoGatheringDialogSubsectionHeader>
      <InfoGatheringDialogFieldGrid>{children}</InfoGatheringDialogFieldGrid>
    </>
  )
}

export interface EditRefundSameAddressCheckboxProps {
  checked: boolean
  onToggle: () => unknown
}

export function EditRefundSameAddressCheckbox({
  checked,
  onToggle,
}: EditRefundSameAddressCheckboxProps) {
  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control -- Checkbox renders a native input nested inside this label
    <label className={styles.checkboxRow}>
      <Checkbox checked={checked} onChange={onToggle} />
      <span>Use same address as contact information</span>
    </label>
  )
}
